import { z } from 'zod';

import { PaginationSchema } from '../../../pagination.js';
import {
  CatalogDefaultAccountKeySchema,
  CatalogDefaultAccountKeysBodySchema,
} from '../products/product.schemas.js';

export const RegisterCategoryBodySchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    slug: z
      .string()
      .trim()
      .min(1)
      .max(100)
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    parentId: z.uuid().optional(),
  })
  .extend(CatalogDefaultAccountKeysBodySchema.shape);

export const RegisterCategoryResponseSchema = z.object({
  categoryId: z.uuid(),
});

export const UpdateCategoryBodySchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    slug: z
      .string()
      .trim()
      .min(1)
      .max(100)
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .optional(),
    parentId: z.uuid().nullable().optional(),
  })
  .extend(CatalogDefaultAccountKeysBodySchema.shape)
  .refine((body) => Object.keys(body).length > 0, { message: 'At least one field is required' });

export const ArchiveCategoryBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const ListCategoriesQuerySchema = PaginationSchema.extend({
  parentId: z.uuid().optional(),
  q: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().trim().min(2).max(100).optional(),
  ),
  axis: z.enum(['root', 'child']).optional(),
}).refine((query) => !(query.axis === 'root' && query.parentId !== undefined), {
  message: 'axis=root cannot be combined with parentId',
  path: ['parentId'],
});

export const AdoptCategoryBodySchema = z.object({
  catalogCategoryId: z.uuid(),
});

export const AdoptCategoryResponseSchema = z.object({
  categoryId: z.uuid(),
  parentCategoryId: z.uuid().nullable(),
});

export const CategoryListItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  parentId: z.uuid().nullable(),
  catalogCategoryId: z.uuid().nullable(),
  defaultRevenueAccountKey: CatalogDefaultAccountKeySchema.nullable(),
  defaultInboundAccountKey: CatalogDefaultAccountKeySchema.nullable(),
  defaultCogsAccountKey: CatalogDefaultAccountKeySchema.nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const ListCategoriesResponseSchema = z.object({
  items: z.array(CategoryListItemSchema),
  pagination: z.object({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export type AdoptCategoryBody = z.infer<typeof AdoptCategoryBodySchema>;
export type AdoptCategoryResponse = z.infer<typeof AdoptCategoryResponseSchema>;
export type RegisterCategoryBody = z.infer<typeof RegisterCategoryBodySchema>;
export type RegisterCategoryResponse = z.infer<typeof RegisterCategoryResponseSchema>;
export type UpdateCategoryBody = z.infer<typeof UpdateCategoryBodySchema>;
export type ArchiveCategoryBody = z.infer<typeof ArchiveCategoryBodySchema>;
export type ListCategoriesQuery = z.infer<typeof ListCategoriesQuerySchema>;
export type ListCategoriesResponse = z.infer<typeof ListCategoriesResponseSchema>;
